import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Rocket, Zap, Crown, PartyPopper, X, AlertCircle, RotateCcw } from "lucide-react";
import { useSubscription, SubscriptionTier } from "@/hooks/useSubscription";
import { useState } from "react";
import { toast } from "sonner";
import analytics from "@/lib/segment";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

type BillingInterval = "monthly" | "annual";

interface PlanFeature {
  text: string;
  included: boolean;
}

interface Plan {
  tier: SubscriptionTier;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  description: string;
  icon: React.ReactNode;
  features: PlanFeature[];
  vehicleLimit: string;
  popular?: boolean;
}

const plans: Plan[] = [
  {
    tier: "free",
    name: "Free",
    monthlyPrice: 0,
    annualPrice: 0,
    description: "Perfect for new or part-time hosts with 1 car",
    icon: <Zap className="h-6 w-6" />,
    vehicleLimit: "1 vehicle",
    features: [
      { text: "Free guides for getting to your first listing", included: true },
      { text: "Add and track your first vehicle", included: true },
      { text: "Log and categorize expenses", included: true },
      { text: "Import trips and earnings from Turo", included: true },
      { text: "View progress to profitability", included: true },
      { text: "Track maintenance timeline", included: true },
      { text: "Community support", included: true },
    ],
  },
  {
    tier: "pro",
    name: "Pro",
    monthlyPrice: 29,
    annualPrice: 23,
    description: "For growing hosts with 2-5 cars",
    icon: <Rocket className="h-6 w-6" />,
    vehicleLimit: "Up to 5 vehicles",
    popular: true,
    features: [
      { text: "Add and track your vehicles", included: true },
      { text: "Log and categorize expenses", included: true },
      { text: "Import trips and earnings from Turo", included: true },
      { text: "View per-car progress to profitability", included: true },
      { text: "Track maintenance timelines", included: true },
      { text: "Utilization charts & advanced metrics", included: true },
      { text: "Ask Captain AI any question about your fleet", included: true },
      { text: "Vehicle-to-vehicle comparisons", included: true },
      { text: "Profit improvement insights", included: true },
      { text: "Priority email support", included: true },
    ],
  },
  {
    tier: "scale",
    name: "Scale",
    monthlyPrice: 79,
    annualPrice: 63,
    description: "For scaling fleets with 6-15 cars",
    icon: <Crown className="h-6 w-6" />,
    vehicleLimit: "Up to 15 vehicles",
    features: [
      { text: "Add and track your vehicles", included: true },
      { text: "Log and categorize expenses", included: true },
      { text: "Import trips and earnings from Turo", included: true },
      { text: "View per-car progress to profitability", included: true },
      { text: "Track maintenance timelines", included: true },
      { text: "Utilization charts & advanced metrics", included: true },
      { text: "Ask Captain AI any question about your fleet", included: true },
      { text: "Vehicle-to-vehicle comparisons", included: true },
      { text: "Profit improvement insights", included: true },
      { text: "Surface risks in your fleet", included: true },
      { text: "Insights on where to focus your attention", included: true },
      { text: "Phone & email support", included: true },
    ],
  },
  {
    tier: "fleet",
    name: "Fleet",
    monthlyPrice: 199,
    annualPrice: 199,
    description: "For large fleets with unlimited vehicles",
    icon: <Crown className="h-6 w-6" />,
    vehicleLimit: "Unlimited vehicles",
    features: [
      { text: "Add and track your vehicles", included: true },
      { text: "Log and categorize expenses", included: true },
      { text: "Import trips and earnings from Turo", included: true },
      { text: "View per-car progress to profitability", included: true },
      { text: "Track maintenance timelines", included: true },
      { text: "Utilization charts & advanced metrics", included: true },
      { text: "Ask Captain AI any question about your fleet", included: true },
      { text: "Vehicle-to-vehicle comparisons", included: true },
      { text: "Profit improvement insights", included: true },
      { text: "Surface risks in your fleet", included: true },
      { text: "Insights on where to focus your attention", included: true },
      { text: "Capital allocation insights", included: true },
      { text: "Depreciation curve analysis", included: true },
      { text: "Dedicated support specialist", included: true },
      { text: "Included setup and training", included: true },
      { text: "Priority phone support", included: true },
    ],
  },
];

const planLabels: Record<SubscriptionTier, string> = {
  free: "Free",
  pro: "Pro",
  scale: "Scale",
  fleet: "Fleet",
};

// Tier order for comparison (higher index = higher tier)
const tierOrder: SubscriptionTier[] = ["free", "pro", "scale", "fleet"];

const getTierIndex = (tier: SubscriptionTier): number => tierOrder.indexOf(tier);

const Upgrade = () => {
  const queryClient = useQueryClient();
  const { 
    subscriptionTier, 
    createCheckoutSession, 
    upgradeSubscription, 
    reactivateSubscription,
    isProOrAbove,
    cancelAtPeriodEnd,
    currentPeriodEnd,
    refetch,
    isLoading: isSubscriptionLoading,
  } = useSubscription();
  const [loadingTier, setLoadingTier] = useState<SubscriptionTier | null>(null);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("monthly");
  const [justUpgradedTo, setJustUpgradedTo] = useState<SubscriptionTier | null>(null);
  const [wasDowngrade, setWasDowngrade] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const [justCancelled, setJustCancelled] = useState(false);
  const [justReactivated, setJustReactivated] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);

  // Use the just-upgraded tier if available, otherwise use the subscription tier from the hook
  const currentTier = justUpgradedTo || subscriptionTier;
  
  // Show pending cancellation if the subscription is set to cancel (unless just reactivated)
  const showPendingCancellation = (cancelAtPeriodEnd || justCancelled) && !justReactivated;

  const handleSelectPlan = async (tier: SubscriptionTier) => {
    const price = tier === "free" ? 0 : tier === "fleet" ? 199 :
      billingInterval === "annual" 
        ? (tier === "pro" ? 23 : 63)
        : (tier === "pro" ? 29 : 79);

    // Track plan selection from upgrade page
    analytics.track("Plan Selected", {
      plan_tier: tier,
      plan_price: price,
      billing_interval: billingInterval,
      source: "upgrade_page",
      current_tier: currentTier,
    });

    if (tier === currentTier && !showPendingCancellation) {
      toast.info("You're already on this plan");
      return;
    }

    // Handle downgrade to free - schedule cancellation
    if (tier === "free") {
      setLoadingTier(tier);
      try {
        toast.info("Scheduling subscription cancellation...");
        await upgradeSubscription("free", "monthly");
        
        // Update local state to show pending cancellation
        setJustCancelled(true);
        setJustReactivated(false);
        
        queryClient.invalidateQueries({ queryKey: ["subscription"] });
        queryClient.invalidateQueries({ queryKey: ["company"] });
        
        toast.success("Your subscription will be cancelled at the end of the billing period.");
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        toast.error(errorMessage);
      } finally {
        setLoadingTier(null);
      }
      return;
    }

    setLoadingTier(tier);
    try {
      // Track checkout started
      analytics.track("Checkout Started", {
        plan_tier: tier,
        plan_price: price,
        billing_interval: billingInterval,
        source: "upgrade_page",
        current_tier: currentTier,
      });
      
      // If user already has a paid subscription, use direct API upgrade
      if (isProOrAbove || justUpgradedTo) {
        const isDowngrade = getTierIndex(tier) < getTierIndex(currentTier);
        const action = isDowngrade ? "Downgrading" : "Upgrading";
        const actionPast = isDowngrade ? "downgraded" : "upgraded";
        
        toast.info(`${action} your subscription...`);
        await upgradeSubscription(tier, billingInterval);
        
        // Update local state immediately instead of reloading
        setJustUpgradedTo(tier);
        setWasDowngrade(isDowngrade);
        setShowCongrats(true);
        
        // Optimistically update the company cache so sidebar nav updates instantly
        queryClient.setQueryData(["company"], (old: any) => 
          old ? { ...old, subscription_tier: tier } : old
        );
        
        // Invalidate the subscription query so other pages (Settings, etc.) get fresh data
        queryClient.invalidateQueries({ queryKey: ["subscription"] });
        queryClient.invalidateQueries({ queryKey: ["company"] });
        
        toast.success(`Successfully ${actionPast} to ${planLabels[tier]}!`);
      } else {
        await createCheckoutSession(tier, billingInterval);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      
      // Track checkout error with technical details for debugging
      analytics.track("Error Occurred", {
        error_type: "checkout",
        error_message: errorMessage,
        plan_tier: tier,
      });
      
      // Show user-friendly message instead of technical errors
      const isEdgeFunctionError = errorMessage.includes("non-2xx") || errorMessage.includes("Edge Function");
      const userFriendlyMessage = isEdgeFunctionError 
        ? "Something went wrong while upgrading your plan. Please try again or contact support."
        : errorMessage;
      toast.error(userFriendlyMessage);
    } finally {
      setLoadingTier(null);
    }
  };

  const handleReactivate = async () => {
    setIsReactivating(true);
    try {
      toast.info("Reactivating your subscription...");
      await reactivateSubscription();
      
      // Update local state
      setJustReactivated(true);
      setJustCancelled(false);
      
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
      queryClient.invalidateQueries({ queryKey: ["company"] });
      refetch();
      
      toast.success("Your subscription has been reactivated!");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(errorMessage);
    } finally {
      setIsReactivating(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-8">
        {/* Pending Cancellation Banner */}
        {showPendingCancellation && (
          <Alert className="bg-warning/10 border-warning/30 relative">
            <AlertCircle className="h-5 w-5 text-warning" />
            <AlertTitle className="text-warning font-semibold">
              Subscription Ending Soon
            </AlertTitle>
            <AlertDescription className="text-warning/90">
              Your {planLabels[currentTier]} subscription will be cancelled{" "}
              {currentPeriodEnd ? `on ${format(new Date(currentPeriodEnd), "MMMM d, yyyy")}` : "at the end of your billing period"}.
              You'll still have access until then.
              <Button
                variant="outline"
                size="sm"
                className="ml-4 border-warning text-warning hover:bg-warning/10"
                onClick={handleReactivate}
                disabled={isReactivating}
              >
                {isReactivating ? (
                  <>Reactivating...</>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Keep My Subscription
                  </>
                )}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Congratulations Banner */}
        {showCongrats && justUpgradedTo && (
          <Alert className="bg-success/10 border-success/30 relative">
            <PartyPopper className="h-5 w-5 text-success" />
            <AlertTitle className="text-success font-semibold">
              {wasDowngrade ? "Plan Changed" : "Congratulations! 🎉"}
            </AlertTitle>
            <AlertDescription className="text-success/90">
              You've successfully {wasDowngrade ? "downgraded" : "upgraded"} to the {planLabels[justUpgradedTo]} plan.{" "}
              {wasDowngrade ? "Your billing has been adjusted." : "Enjoy your new features!"}
            </AlertDescription>
            <button
              onClick={() => setShowCongrats(false)}
              className="absolute top-3 right-3 text-success/70 hover:text-success transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </Alert>
        )}

        <div className="text-center max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Upgrade Your Plan
          </h1>
          <p className="text-muted-foreground">
            Unlock more vehicles and features to grow your hosting business
          </p>
        </div>



        <div className="grid gap-6 lg:grid-cols-4 max-w-6xl mx-auto items-stretch">
          {plans.map((plan) => {
            const displayPrice = billingInterval === "annual" ? plan.annualPrice : plan.monthlyPrice;
            
            return (
              <Card
                key={plan.tier}
                className={`relative flex flex-col ${
                  plan.popular
                    ? "border-primary shadow-lg"
                    : ""
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-4 flex-shrink-0">
                  <div className={`mx-auto p-3 rounded-full mb-3 ${
                    plan.tier === currentTier
                      ? "bg-primary/20 text-primary"
                      : "bg-muted text-muted-foreground"
                  }`}>
                    {plan.icon}
                  </div>
                  <CardTitle>{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    {false ? (
                      <>
                        <span className="text-4xl font-bold">Custom</span>
                        <span className="text-sm text-muted-foreground ml-1">pricing</span>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold">${displayPrice}</span>
                        <span className="text-muted-foreground">/mo</span>
                      </>
                    )}
                  </div>
                  {billingInterval === "annual" && plan.tier !== "free" && plan.tier !== "fleet" && (
                    <p className="text-xs text-success mt-1">
                      Billed ${displayPrice * 12}/year
                    </p>
                  )}
                </CardHeader>
                <CardContent className="flex flex-col flex-grow">
                  <div className="text-center py-2 px-4 bg-muted rounded-lg mb-4">
                    <span className="font-medium">{plan.vehicleLimit}</span>
                  </div>
                  <ul className="space-y-2 flex-grow">
                    {plan.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className={`flex items-center gap-2 text-sm ${
                          feature.included
                            ? "text-foreground"
                            : "text-muted-foreground line-through"
                        }`}
                      >
                        <Check
                          className={`h-4 w-4 flex-shrink-0 ${
                            feature.included
                              ? "text-green-500"
                              : "text-muted-foreground"
                          }`}
                        />
                        {feature.text}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-8 space-y-1">
                    <Button
                      className="w-full"
                      variant={
                        (plan.tier === currentTier && !showPendingCancellation) ||
                        (plan.tier === "free" && showPendingCancellation)
                          ? "outline"
                          : "default"
                      }
                      disabled={
                        (plan.tier === currentTier && !showPendingCancellation) ||
                        (plan.tier === "free" && showPendingCancellation) ||
                        loadingTier !== null ||
                        (plan.tier === currentTier && showPendingCancellation && plan.tier !== "free")
                      }
                      onClick={() => handleSelectPlan(plan.tier)}
                    >
                      {loadingTier === plan.tier
                        ? "Loading..."
                        : plan.tier === "free" && showPendingCancellation
                        ? "Downgrading to Free"
                        : plan.tier === currentTier && showPendingCancellation
                        ? "Ending Soon"
                        : plan.tier === currentTier && !showPendingCancellation
                        ? "Current Plan"
                        : plan.tier === "free"
                        ? "Downgrade to Free"
                        : getTierIndex(plan.tier) < getTierIndex(currentTier)
                        ? `Downgrade to ${plan.name}`
                        : `Upgrade to ${plan.name}`}
                    </Button>
                    {/* Helper text for cancellation pending state - show on the paid tier that is ending */}
                    {showPendingCancellation && plan.tier === subscriptionTier && subscriptionTier !== "free" && currentPeriodEnd && (
                      <p className="text-xs text-muted-foreground text-center">
                        Access to {planLabels[subscriptionTier]} ends {format(new Date(currentPeriodEnd), "MMMM d, yyyy")}
                      </p>
                    )}
                    {showPendingCancellation && plan.tier !== currentTier && plan.tier !== "free" && (
                      <p className="text-xs text-muted-foreground text-center">
                        Upgrading starts your subscription again.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Pending cancellation info box */}
        {showPendingCancellation && currentPeriodEnd && (
          <Alert className="bg-warning/10 border-warning/30 max-w-3xl mx-auto">
            <AlertCircle className="h-4 w-4 text-warning" />
            <AlertDescription className="text-warning/90 text-sm">
              Your {planLabels[currentTier]} plan has been cancelled but you'll continue to have full access until{" "}
              <span className="font-semibold">{format(new Date(currentPeriodEnd), "MMMM d, yyyy")}</span>.
              After that date, your account will automatically switch to the Free plan.
            </AlertDescription>
          </Alert>
        )}

      </div>
    </AppLayout>
  );
};

export default Upgrade;